import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import DepartmentService from "../../core/services/DepartmentService";
import roleService from "../../core/services/RoleService";
import { useDepartments } from "../../core/DepartmentsContext";
import {
  getDepartmentColor,
  getDepartmentHeadRoleId,
  getDepartmentHeadRoleName,
  getDepartmentIcon,
} from "../../core/constants/departments";
import "../../styles/UserProfile.css";

function resolveDepartmentId(department, catalog) {
  if (!department) return null;

  if (department._id) {
    return String(department._id);
  }

  const match = catalog?.find(
    (entry) => entry.name === department.name && entry._id,
  );

  return match?._id ? String(match._id) : null;
}

function EditDepartmentModal({
  show,
  onHide,
  department,
  catalog,
  onSaved,
}) {
  const { refreshDepartments } = useDepartments();
  const [name, setName] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [headRoleId, setHeadRoleId] = useState("");
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverError, setCoverError] = useState(null);
  const [error, setError] = useState(null);

  const departmentId = useMemo(
    () => resolveDepartmentId(department, catalog),
    [department, catalog],
  );

  useEffect(() => {
    if (!show || !department) return;

    setName(department.name || "");
    setCoverImage(department.coverImage || "");
    setCoverError(null);
    setError(null);
  }, [show, department]);

  useEffect(() => {
    if (!show) return;

    let cancelled = false;

    const loadRoles = async () => {
      setLoadingRoles(true);
      try {
        const data = await roleService.getAllRoles();
        if (cancelled) return;
        setRoles(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load roles:", err);
        if (!cancelled) {
          setRoles([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRoles(false);
        }
      }
    };

    loadRoles();

    return () => {
      cancelled = true;
    };
  }, [show]);

  useEffect(() => {
    if (!show || !department || loadingRoles) return;
    setHeadRoleId(getDepartmentHeadRoleId(department, catalog, roles) || "");
  }, [show, department, catalog, roles, loadingRoles]);

  const roleOptions = useMemo(() => {
    return [...roles].sort((a, b) => a.name.localeCompare(b.name));
  }, [roles]);

  const selectedRoleName = useMemo(() => {
    if (!headRoleId) return null;
    return roleOptions.find((role) => String(role._id) === String(headRoleId))?.name || null;
  }, [headRoleId, roleOptions]);

  const handleCoverImageChange = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file || !departmentId) return;

    setUploadingCover(true);
    setCoverError(null);

    try {
      const updated = await DepartmentService.uploadCoverImage(departmentId, file);
      setCoverImage(updated.coverImage || "");
      await refreshDepartments();
    } catch (err) {
      setCoverError(err.message || "Failed to upload cover image.");
    } finally {
      setUploadingCover(false);
      event.target.value = "";
    }
  }, [departmentId, refreshDepartments]);

  const handleDeleteCoverImage = useCallback(async () => {
    if (!departmentId || !coverImage) return;

    setUploadingCover(true);
    setCoverError(null);

    try {
      await DepartmentService.deleteCoverImage(departmentId);
      setCoverImage("");
      await refreshDepartments();
    } catch (err) {
      setCoverError(err.message || "Failed to remove cover image.");
    } finally {
      setUploadingCover(false);
    }
  }, [coverImage, departmentId, refreshDepartments]);

  const handleSave = useCallback(async () => {
    if (!department) return;

    if (!departmentId) {
      setError("This department cannot be saved because it has no database id.");
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Department name is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        name: trimmedName,
      };

      if (department.hasHeadOfRole) {
        payload.headRoleId = headRoleId || null;
      }

      await DepartmentService.updateDepartment(departmentId, payload);
      await onSaved?.();
      onHide();
    } catch (err) {
      setError(err.message || "Failed to update department.");
    } finally {
      setSaving(false);
    }
  }, [
    department,
    departmentId,
    headRoleId,
    name,
    onHide,
    onSaved,
  ]);

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSave();
  };

  if (!department) return null;

  const fallbackHeadRoleName = getDepartmentHeadRoleName(department, catalog);

  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Department</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Cover image</Form.Label>
            <div className="department-edit-cover-upload">
              {coverImage ? (
                <div className="department-edit-cover-preview">
                  <img src={coverImage} alt="" />
                </div>
              ) : (
                <div className="department-edit-cover-placeholder">
                  {getDepartmentIconFallback(department, catalog)}
                </div>
              )}
              <div className="department-edit-cover-actions">
                <label className={`file-upload-btn ${uploadingCover ? "disabled" : ""}`}>
                  {uploadingCover ? "Uploading..." : "CHOOSE PHOTO"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    disabled={uploadingCover || !departmentId}
                    className="file-upload-input"
                  />
                </label>
                {coverImage && (
                  <button
                    type="button"
                    onClick={handleDeleteCoverImage}
                    disabled={uploadingCover}
                    className="file-delete-btn"
                  >
                    REMOVE PHOTO
                  </button>
                )}
              </div>
              {coverError && <p className="department-edit-cover-error">{coverError}</p>}
            </div>
          </Form.Group>

          {department.hasHeadOfRole && (
            <Form.Group className="mb-3">
              <Form.Label>Head role</Form.Label>
              <Form.Select
                value={headRoleId}
                onChange={(e) => setHeadRoleId(e.target.value)}
                disabled={loadingRoles}
              >
                <option value="">
                  {loadingRoles ? "Loading roles..." : "No head role assigned"}
                </option>
                {roleOptions.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
              {selectedRoleName && (
                <Form.Text className="text-muted">
                  Users assigned to &quot;{selectedRoleName}&quot; will appear as the department head.
                </Form.Text>
              )}
              {!headRoleId && fallbackHeadRoleName && (
                <Form.Text className="text-muted">
                  Default role for this department: {fallbackHeadRoleName}
                </Form.Text>
              )}
            </Form.Group>
          )}

          {error && <div className="department-edit-error">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || uploadingCover}
          >
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function getDepartmentIconFallback(department, catalog) {
  const color = getDepartmentColor(department, catalog);

  return (
    <div
      className="department-edit-cover-icon-fallback"
      style={{ "--department-color": color }}
    >
      {getDepartmentIcon(
        department,
        { className: "department-edit-cover-icon" },
        catalog,
      )}
    </div>
  );
}

export default EditDepartmentModal;
